import {
  ArrowDownwardOutlined as DownIcon,
  ArrowUpwardOutlined as UpIcon,
  CloseOutlined as RemoveIcon,
} from '@mui/icons-material';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';

import type { ProviderOption } from '../../services/accountService';

interface Props {
  value: string[];
  providers: ProviderOption[];
  onChange: (next: string[]) => void;
}

/**
 * Orden en que se intentan los proveedores de ESTA cuenta. El backend guarda la
 * prioridad en la relación cuenta-proveedor, así que el orden de esta lista es el
 * que acaba mandando: el primero de cada canal es el que se usa, y los siguientes
 * son el respaldo si falla.
 *
 * No se ordena entre canales: un proveedor de email y uno de Telegram nunca
 * compiten entre sí, el motor filtra por canal antes de recorrer la lista.
 */
export const ProviderPriorityList = ({ value, providers, onChange }: Props) => {
  const translate = useTranslate();

  // Con menos de dos proveedores el orden no significa nada.
  if (value.length < 2) return null;

  const move = (from: number, to: number) => {
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const remove = (id: string) => onChange(value.filter((x) => x !== id));

  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {translate('resources.accounts.fields.providerOrder', {
          _: 'Orden de intento de los proveedores',
        })}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
        {translate('resources.accounts.help.providerOrder', {
          _: 'Dentro de cada canal se intenta primero el de más arriba; los siguientes actúan como respaldo.',
        })}
      </Typography>

      <Stack spacing={0.5}>
        {value.map((id, index) => {
          const provider = providers.find((p) => p.id === id);
          const channels = provider?.communicationType ?? [];
          return (
            <Stack
              key={id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                backgroundColor: 'background.paper',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', minWidth: 18, textAlign: 'right' }}
              >
                {index + 1}
              </Typography>

              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {provider?.providerName ??
                  translate('resources.accounts.fields.providerUnavailable', {
                    _: 'Unavailable provider',
                  })}
              </Typography>

              {channels.map((channel) => (
                <Chip key={channel} label={channel} size="small" variant="outlined" />
              ))}

              <Tooltip title={translate('ra.action.move_up', { _: 'Subir' })}>
                <span>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                  >
                    <UpIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={translate('ra.action.move_down', { _: 'Bajar' })}>
                <span>
                  <IconButton
                    size="small"
                    disabled={index === value.length - 1}
                    onClick={() => move(index, index + 1)}
                  >
                    <DownIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={translate('ra.action.remove', { _: 'Quitar' })}>
                <IconButton size="small" onClick={() => remove(id)}>
                  <RemoveIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};
